import { Component, ChangeDetectionStrategy, signal, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { Router, RouterLink } from '@angular/router';
import { PERMISSION_UI_DIRECTIVES } from '../../directives/permission.directive';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ProductTaxService, ProductTaxDTO, ProductTaxFilterSpec } from '../../services/product-tax.service';
import { ProductTaxReadComponent } from './product-tax-read.component';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { DrawerModule } from 'primeng/drawer';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { EntityState } from '../../core/models';

@Component({
  selector: 'app-product-taxes-list',
  templateUrl: './product-taxes-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1 1 0%;
        min-height: 0;
        width: 100%;
      }
      :host ::ng-deep .product-taxes-primeng-table .p-datatable-thead > tr > th {
        background: rgb(248 250 252);
        border-color: rgb(226 232 240);
        color: rgb(100 116 139);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 0.55rem 0.5rem !important;
        transition: background-color 0.15s ease, color 0.15s ease;
      }
      :host ::ng-deep .product-taxes-primeng-table .p-datatable-thead > tr > th.p-sortable-column {
        cursor: pointer;
        user-select: none;
      }
      :host ::ng-deep .product-taxes-primeng-table .p-datatable-thead > tr > th.p-sortable-column:hover {
        background: rgb(241 245 249);
        color: rgb(15 23 42);
      }
      :host ::ng-deep .product-taxes-primeng-table .p-datatable-thead > tr > th.p-sortable-column:focus-visible {
        outline: 2px solid rgb(14 165 233);
        outline-offset: -2px;
      }
      :host ::ng-deep .product-taxes-primeng-table .p-datatable-thead > tr > th.p-sortable-column.p-datatable-column-sorted {
        background: rgb(240 249 255);
        color: rgb(3 105 161);
      }
      :host ::ng-deep .product-taxes-primeng-table .p-datatable-thead > tr > th.p-sortable-column .p-datatable-sort-icon,
      :host ::ng-deep .product-taxes-primeng-table .p-datatable-thead > tr > th.p-sortable-column .p-sortable-column-icon,
      :host ::ng-deep .product-taxes-primeng-table .p-datatable-thead > tr > th.p-sortable-column p-sorticon svg {
        width: 0.75rem;
        height: 0.75rem;
        color: rgb(14 165 233);
        opacity: 1;
        flex-shrink: 0;
        margin-left: auto;
      }
      :host ::ng-deep .product-taxes-primeng-table .p-datatable-tbody > tr > td {
        padding: 0.4rem 0.45rem !important;
      }
      :host ::ng-deep p-table {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .p-datatable-table-container {
        flex: 1 1 0;
        min-height: 0;
        overflow: auto;
      }
      :host ::ng-deep .product-tax-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .product-tax-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
    `
  ],
  imports: [
    CommonModule,
    PERMISSION_UI_DIRECTIVES,
    RouterLink,
    MatIconModule,
    ReactiveFormsModule,
    MatButtonModule,
    FloatLabelModule,
    SelectModule,
    DrawerModule,
    ProductTaxReadComponent,
    TableModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    TagModule,
    ButtonModule,
    SkeletonModule
  ]
})
export class ProductTaxesListComponent {
  private productTaxService = inject(ProductTaxService);
  private fb = inject(FormBuilder);
  private router = inject(Router);

  selectedProductTax = signal<ProductTaxDTO | null>(null);
  isDetailsOpen = signal(false);
  deleteModalOpen = signal(false);
  deleteModalShown = signal(false);
  productTaxToDelete = signal<ProductTaxDTO | null>(null);
  deleteLoading = signal(false);

  productTaxes = signal<ProductTaxDTO[]>([]);
  loading = signal(false);
  initialLoadDone = signal(false);
  error = signal<string | null>(null);
  detailsLoading = signal(false);
  currentPage = signal(0);
  pageSize = signal(20);
  totalItems = signal(0);
  totalPages = signal(0);
  pageSizeOptions = [20, 50, 100];
  sortField = signal('id');
  sortOrder = signal(-1);
  skeletonRows = [1, 2, 3, 4, 5, 6, 7, 8];

  private readonly allFilterValue = 'ALL';

  filtersForm = this.fb.group({
    q: [''],
    state: [EntityState.ACTIVE]
  });

  hasActiveFilters = signal(false);

  stateFilterOptions = [
    { value: this.allFilterValue, label: 'Todos' },
    { value: EntityState.ACTIVE, label: 'Activo' },
    { value: EntityState.INACTIVE, label: 'Inactivo' }
  ];

  constructor() {
    this.filtersForm.controls.q.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntilDestroyed()
    ).subscribe(() => this.applyFilters());

    this.filtersForm.controls.state.valueChanges.pipe(
      takeUntilDestroyed()
    ).subscribe(() => this.applyFilters());

    this.loadProductTaxes();
  }

  loadProductTaxes(): void {
    this.loading.set(true);
    this.error.set(null);

    const spec = this.buildSpecFromFilters();
    const direction = this.sortOrder() === 1 ? 'asc' : 'desc';
    const pageable = {
      page: this.currentPage(),
      size: this.pageSize(),
      sort: [`${this.sortField()},${direction}`]
    };

    this.productTaxService.getProductTaxes(spec, pageable).subscribe({
      next: (response) => {
        if (response.ok) {
          this.productTaxes.set(response.body);
          if (response.page) {
            this.totalItems.set(response.page.totalItems);
            this.totalPages.set(response.page.totalPages);
            this.pageSize.set(response.page.pageSize);
            this.currentPage.set(Math.max(0, response.page.currentPage - 1));
          }
        } else {
          this.error.set(response.message || 'Error al cargar impuestos');
        }
        this.loading.set(false);
        this.initialLoadDone.set(true);
      },
      error: () => {
        this.error.set('Error al conectar con el servidor.');
        this.loading.set(false);
        this.initialLoadDone.set(true);
      }
    });
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    const size = event.rows || this.pageSize();
    const rawSortField = Array.isArray(event.sortField) ? event.sortField[0] : event.sortField;
    const nextSortField = rawSortField || this.sortField();
    const nextSortOrder = event.sortOrder ?? this.sortOrder();
    const sortChanged =
      nextSortField !== this.sortField() || nextSortOrder !== this.sortOrder();
    const page = sortChanged
      ? 0
      : event.rows
        ? Math.floor((event.first ?? 0) / event.rows)
        : 0;

    if (!sortChanged && page === this.currentPage() && size === this.pageSize()) return;

    this.sortField.set(nextSortField);
    this.sortOrder.set(nextSortOrder);
    this.currentPage.set(page);
    this.pageSize.set(size);
    this.loadProductTaxes();
  }

  openDetails(item: ProductTaxDTO): void {
    if (!item?.id) return;
    this.isDetailsOpen.set(true);
    this.selectedProductTax.set(null);
    this.detailsLoading.set(true);
    this.productTaxService.getProductTaxById(item.id).subscribe({
      next: (response) => {
        if (response.ok) {
          this.selectedProductTax.set(response.body);
        } else {
          this.error.set(response.message || 'Error al cargar el impuesto');
        }
        this.detailsLoading.set(false);
      },
      error: () => {
        this.error.set('Error al cargar el impuesto');
        this.detailsLoading.set(false);
      }
    });
  }

  closeDetails(): void {
    this.isDetailsOpen.set(false);
    this.selectedProductTax.set(null);
    this.detailsLoading.set(false);
  }

  onDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeDetails();
  }

  editFromModal(): void {
    const item = this.selectedProductTax();
    if (!item?.id) return;
    this.closeDetails();
    this.router.navigate(['/product-taxes', item.id]);
  }

  editItem(item: ProductTaxDTO, event?: MouseEvent): void {
    event?.stopPropagation();
    if (!item?.id) return;
    this.router.navigate(['/product-taxes', item.id]);
  }

  openDeleteModal(item: ProductTaxDTO, event?: MouseEvent): void {
    event?.stopPropagation();
    if (!item?.id) return;
    this.productTaxToDelete.set(item);
    this.deleteModalOpen.set(true);
    setTimeout(() => this.deleteModalShown.set(true), 10);
  }

  closeDeleteModal(): void {
    this.deleteModalShown.set(false);
    setTimeout(() => {
      this.deleteModalOpen.set(false);
      this.productTaxToDelete.set(null);
    }, 200);
  }

  confirmDelete(): void {
    const item = this.productTaxToDelete();
    if (!item?.id) return;

    this.deleteLoading.set(true);
    this.productTaxService.deleteProductTax(item.id).subscribe({
      next: (response) => {
        if (response.ok) {
          this.loadProductTaxes();
          this.closeDeleteModal();
        } else {
          this.error.set(response.message || 'Error al eliminar el impuesto');
        }
        this.deleteLoading.set(false);
      },
      error: (err) => {
        this.error.set(err.error?.message || 'Error al eliminar el impuesto');
        this.deleteLoading.set(false);
      }
    });
  }

  applyFilters(): void {
    const raw = this.filtersForm.getRawValue();
    this.hasActiveFilters.set(!!(raw.q?.trim() || raw.state !== EntityState.ACTIVE));
    this.currentPage.set(0);
    this.loadProductTaxes();
  }

  clearFilters(): void {
    this.filtersForm.reset({ q: '', state: EntityState.ACTIVE }, { emitEvent: false });
    this.applyFilters();
  }

  getStateSeverity(state?: string): 'success' | 'danger' | null {
    if (state === 'ACTIVE') return 'success';
    if (state === 'INACTIVE') return 'danger';
    return null;
  }

  applicationTypeLabel(type?: string): string {
    switch (String(type || '').toUpperCase()) {
      case 'TAXABLE': return 'Gravado';
      case 'EXEMPT': return 'Exento';
      case 'ZERO_RATED': return 'Tasa 0%';
      default: return '—';
    }
  }

  collectionTypeLabel(type?: string): string {
    switch (String(type || '').toUpperCase()) {
      case 'SELLER_COLLECTS': return 'Traslado';
      case 'SELLER_WITHHOLDS': return 'Retención';
      default: return '—';
    }
  }

  private buildSpecFromFilters(): ProductTaxFilterSpec {
    const value = this.filtersForm.getRawValue();
    const spec: ProductTaxFilterSpec = {};
    const q = value.q?.trim();
    if (q) {
      spec['q[lk]'] = q;
    }
    if (this.isSpecificFilter(value.state)) {
      spec.state = value.state;
    } else {
      spec.state = [EntityState.ACTIVE, EntityState.INACTIVE];
    }
    return spec;
  }

  private isSpecificFilter(value: string | null | undefined): boolean {
    return !!value && value !== this.allFilterValue;
  }
}
